import * as React from "react";
import {
    ExternalLink,
    Pencil,
    Plus,
    Trash2,
    Brain,
    BookOpen,
    Users,
    MessageSquare,
    Cross,
    ChevronDown,
    ChevronUp,
} from "lucide-react";
import { cn } from "../utils/cn";
import { Card, CardContent, CardFooter } from "./card";
import { Mono, Text } from "../primitives/typography";

export type ResourceCategory = "Mental Health" | "Academics" | "Student Life" | "Counseling" | "Health & Medical";

const CATEGORY_META: Record<
    ResourceCategory,
    { icon: React.ElementType; borderColor: string; badgeBg: string; badgeText: string; iconColor: string }
> = {
    "Mental Health": {
        icon: Brain,
        borderColor: "border-t-violet-400",
        badgeBg: "bg-violet-50 dark:bg-violet-950",
        badgeText: "text-violet-700 dark:text-violet-300",
        iconColor: "text-violet-500",
    },
    Academics: {
        icon: BookOpen,
        borderColor: "border-t-amber-400",
        badgeBg: "bg-amber-50 dark:bg-amber-950",
        badgeText: "text-amber-700 dark:text-amber-300",
        iconColor: "text-amber-500",
    },
    "Student Life": {
        icon: Users,
        borderColor: "border-t-sky-400",
        badgeBg: "bg-sky-50 dark:bg-sky-950",
        badgeText: "text-sky-700 dark:text-sky-300",
        iconColor: "text-sky-500",
    },
    Counseling: {
        icon: MessageSquare,
        borderColor: "border-t-teal-400",
        badgeBg: "bg-teal-50 dark:bg-teal-950",
        badgeText: "text-teal-700 dark:text-teal-300",
        iconColor: "text-teal-500",
    },
    "Health & Medical": {
        icon: Cross,
        borderColor: "border-t-emerald-400",
        badgeBg: "bg-emerald-50 dark:bg-emerald-950",
        badgeText: "text-emerald-700 dark:text-emerald-300",
        iconColor: "text-emerald-500",
    },
};

export interface Resource {
    title: string;
    description?: string | null;
    url?: string | null;
    addedAt?: string | null;
    category?: ResourceCategory | string | null;
}

export interface ResourceCardProps {
    resource?: Resource;
    onEdit?: () => void;
    onDelete?: () => void;
    onClick?: (url: string) => void;
}

function ResourceDescription({ description }: { description: string }) {
    const [
        expanded,
        setExpanded,
    ] = React.useState(false);

    return (
        <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="flex w-full items-start gap-1.5 text-left"
        >
            <Text size="sm" muted className={cn("leading-relaxed", !expanded && "line-clamp-3")}>
                {description}
            </Text>
            {expanded ? (
                <ChevronUp className="text-muted-foreground mt-0.5 h-3.5 w-3.5 shrink-0" />
            ) : (
                <ChevronDown className="text-muted-foreground mt-0.5 h-3.5 w-3.5 shrink-0" />
            )}
        </button>
    );
}

export function ResourceCard({ resource, onEdit, onDelete, onClick, ...props }: ResourceCardProps) {
    const title = resource?.title ?? "";
    const description = resource?.description;
    const url = resource?.url;
    const addedAt = resource?.addedAt;
    const category = resource?.category as ResourceCategory | null | undefined;
    const meta = category ? CATEGORY_META[category] : null;
    const Icon = meta?.icon;

    return (
        <Card
            className={cn(
                "flex min-h-52 w-full max-w-sm flex-col overflow-hidden border-t-4",
                meta ? meta.borderColor : "border-t-border",
            )}
            {...props}
        >
            <CardContent className="grow pt-4 pb-4">
                {meta && Icon && (
                    <div className="mb-3 flex items-center gap-2">
                        <div className={cn("flex h-6 w-6 items-center justify-center rounded", meta.badgeBg)}>
                            <Icon className={cn("h-3.5 w-3.5", meta.iconColor)} />
                        </div>
                        <Mono className={cn("text-[10px] font-semibold tracking-widest uppercase", meta.badgeText)}>
                            {category}
                        </Mono>
                    </div>
                )}
                <Text weight="semibold" className="mb-2 leading-snug">
                    {title}
                </Text>
                {description && <ResourceDescription description={description} />}
                {url && (
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => onClick?.(url)}
                        className="text-secondary hover:text-primary/80 mt-3 inline-flex items-center gap-1.5 text-sm font-bold transition-colors"
                    >
                        <ExternalLink className="h-3.5 w-3.5" />
                        View resource
                    </a>
                )}
            </CardContent>
            <CardFooter className="bg-background border-border border-t px-5 py-3">
                <div className="flex w-full items-center justify-between">
                    {addedAt ? (
                        <Mono size="xs" muted>
                            Added {addedAt}
                        </Mono>
                    ) : (
                        <span />
                    )}
                    <div className="flex items-center gap-6">
                        {onEdit && (
                            <Pencil
                                onClick={onEdit}
                                className="hover:text-foreground h-3.5 w-3.5 cursor-pointer text-neutral-400 transition-colors"
                            />
                        )}
                        {onDelete && (
                            <Trash2
                                onClick={onDelete}
                                className="hover:text-destructive h-3.5 w-3.5 cursor-pointer text-neutral-400 transition-colors"
                            />
                        )}
                    </div>
                </div>
            </CardFooter>
        </Card>
    );
}

/* ---------------------------- AddResourceCard ----------------------------- */

export interface AddResourceCardProps extends React.HTMLAttributes<HTMLButtonElement> {
    label?: string;
    subLabel?: string;
}

const AddResourceCard = React.forwardRef<HTMLButtonElement, AddResourceCardProps>(
    ({ className, label, subLabel, onClick, ...props }, ref) => (
        <button
            ref={ref}
            onClick={onClick}
            className={cn(
                "border-border bg-muted/30 hover:bg-muted/60 flex w-full max-w-sm cursor-pointer flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed px-6 py-10 transition-colors",
                className,
            )}
            {...props}
        >
            <div className="bg-secondary/10 flex h-10 w-10 items-center justify-center rounded-xl">
                <Plus className="text-primary h-5 w-5" />
            </div>
            <div className="flex flex-col items-center gap-1">
                <Text weight="semibold">{label}</Text>
                <Text size="sm" muted className="text-center">
                    {subLabel}
                </Text>
            </div>
        </button>
    ),
);
AddResourceCard.displayName = "AddResourceCard";

export { AddResourceCard };
